<template>
  <div v-if="goal">
    <div
      class="percentage bold align--right color--prim"
      :style="{ width: `${percentage}%` }"
      v-if="block.isShowed('percentage')"
    >
      <app-number :number="percentage">
        <template #default="{ formatedNumber }">
          {{ formatedNumber }}%
        </template>
      </app-number>
    </div>

    <app-progress-bar
      id="progress-bar"
      :value="amount"
      :max-value="goal"
    />

    <ul
      class="statistics"
      :class="{ 'statistics--large': !column.is_small }"
      v-if="block.isShowed('amount') || block.isShowed('goal')"
    >
      <li
        class="statistics__li"
        v-if="block.isShowed('amount')"
      >
        <label
          for="progress-bar"
          class="color--prim bold"
        >
          {{ interpolate(block.getText('amount'), variables) }}
        </label>
      </li>

      <li
        class="statistics__li statistics__li--goal color--lg"
        v-if="block.isShowed('goal')"
      >
        {{ interpolate(block.getText('goal'), variables) }}

        <app-tooltip v-if="currency !== project.currency">
          <button
            class="tooltip"
            :aria-describedby="`${block.id}_tooltip`"
            :aria-label="t('literal.show_amount_collected_in_project_currency')"
          >
            <fa-icon :icon="['fal', 'coins'] " />
          </button>

          <template #popper>
            <p
              :id="`${block.id}_tooltip`"
              role="tooltip"
            >
              <app-money
                :amount="amountCollected"
                :currency="project.currency"
              />

              /

              <app-money
                :amount="goalAmount"
                :currency="project.currency"
              />
            </p>
          </template>
        </app-tooltip>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { computed, defineAsyncComponent, inject } from 'vue'
import { useI18n } from '@/vendors/i18n'
import { useCurrencyStore } from '@/stores/CurrencyStore'
import { useMoney } from '@/composables/app/useMoney'
import { get as _get } from 'lodash'
import { storeToRefs } from 'pinia'

const AppProgressBar = defineAsyncComponent(() => import('&/atoms/AppProgressBar'))
const AppTooltip = defineAsyncComponent(() => import('&/atoms/AppTooltip'))
const AppMoney = defineAsyncComponent(() => import('&/atoms/AppMoney'))
const AppNumber = defineAsyncComponent(() => import('&/atoms/AppNumber'))

defineProps({
  column: { type: Object, required: true },
  block: { type: Object, required: true }
})

const { t, interpolate } = useI18n()
const { currency } = storeToRefs(useCurrencyStore())
const { getAmount, getFormatedAmount } = useMoney()

const objects = inject('objects', {})

const project = computed(() => _get(objects.value, 'project'))
const statistics = computed(() => _get(objects.value, 'statistics', {}))

const goalAmount = computed(() => project.value?.goal_amount)
const amountCollected = computed(() => statistics.value?.amount_collected)

const amount = computed(() => getAmount({ amount: amountCollected }))
const goal = computed(() => getAmount({ amount: goalAmount }) )
const percentage = computed(() => Math.round((amount.value / goal.value) * 100))

const variables = computed(() => {
  return ({
    ...statistics.value,
    amount_collected: getFormatedAmount({ amount: amount.value }),
    goal: getFormatedAmount({ amount: goal.value })
  })
})
</script>

<style lang="scss" scoped>
.percentage {
  max-width: 100%;
  font-size: rem(14px);
}

.statistics {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;

  &--large {
    @include mq(sm) {
      font-size: rem(18px);
    }
  }

  &__li {
    display: flex;
    align-items: center;
    gap: 6px;

    &--goal {
      margin-left: auto;
    }
  }
}

.tooltip {
  color: $vlg;
}
</style>